import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Download } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safe date formatting helper
const safeFormatDate = (dateString, formatStr = "MMM dd, yyyy") => {
  if (!dateString) return "N/A";
  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? format(parsed, formatStr) : "N/A";
  } catch {
    return "N/A";
  }
};

// A4 dimensions in pixels at 96 DPI
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const PAGE_PADDING = 60;

export default function PrintInvoice() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderResponse, settingsResponse] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`),
          axios.get(`${API}/settings`)
        ]);
        setOrder(orderResponse.data);
        setSettings(settingsResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice-${order?.client_name.replace(/\s+/g, '-')}-${orderId.slice(0, 8)}`,
    pageStyle: `
      @page { size: A4; margin: 0; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .a4-page { page-break-after: always; page-break-inside: avoid; }
        .a4-page:last-child { page-break-after: auto; }
        .no-print { display: none !important; }
      }
    `
  });

  const handleSaveAsJPG = async () => {
    setSaving(true);
    try {
      const pages = printRef.current.querySelectorAll('.a4-page');
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: A4_WIDTH_PX,
          height: page.scrollHeight,
          windowWidth: A4_WIDTH_PX,
          windowHeight: page.scrollHeight
        });
        const image = canvas.toDataURL('image/jpeg', 0.95);
        const link = document.createElement('a');
        const pageNum = pages.length > 1 ? `-page${i + 1}` : '';
        link.download = `Invoice-${order.client_name.replace(/\s+/g, '-')}-${orderId.slice(0, 8)}${pageNum}.jpg`;
        link.href = image;
        link.click();
        if (i < pages.length - 1) await new Promise(resolve => setTimeout(resolve, 500));
      }
      toast.success(`Invoice saved as ${pages.length > 1 ? pages.length + ' JPG files' : 'JPG'}!`);
    } catch (error) {
      console.error("Error saving as JPG:", error);
      toast.error("Failed to save as JPG");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold mb-2">Order not found</h2>
          <Button onClick={() => navigate("/")} data-testid="back-to-dashboard-invoice-button">
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const subtotal = order.total_amount;
  
  // Tax calculation
  const taxSettings = settings?.tax_settings || { enabled: false, tax_rate: 0, tax_name: "VAT", include_in_price: false };
  let taxAmount = 0;
  let subtotalBeforeTax = subtotal;
  let total = subtotal;
  
  if (taxSettings.enabled && taxSettings.tax_rate > 0) {
    if (taxSettings.include_in_price) {
      // Prices already include tax - calculate backwards
      subtotalBeforeTax = subtotal / (1 + taxSettings.tax_rate / 100);
      taxAmount = subtotal - subtotalBeforeTax;
      total = subtotal; // Total stays the same since tax is included
    } else {
      // Tax is added on top
      taxAmount = subtotal * (taxSettings.tax_rate / 100);
      subtotalBeforeTax = subtotal;
      total = subtotal + taxAmount;
    }
  }
  
  const hasPhotos = order.photos?.length > 0 || order.photo;
  
  // Page 2 only if more than 10 items
  const needsSecondPage = order.items.length > 10;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header - No Print */}
      <header className="bg-white border-b border-border sticky top-0 z-40 no-print">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate("/")} data-testid="invoice-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
          <div className="flex gap-2">
            <Button onClick={handleSaveAsJPG} variant="outline" disabled={saving} data-testid="invoice-save-jpg-button">
              <Download className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save as JPG"}
            </Button>
            <Button onClick={handlePrint} className="bg-primary" data-testid="invoice-print-button">
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div ref={printRef} className="print-wrapper">
          {/* Page 1 */}
          <div 
            className="a4-page bg-white mx-auto mb-8 shadow-lg relative"
            style={{ width: `${A4_WIDTH_PX}px`, minHeight: `${A4_HEIGHT_PX}px`, padding: `${PAGE_PADDING}px`, boxSizing: 'border-box' }}
          >
            {/* Header */}
            <div className="mb-6 pb-4 border-b-2 border-primary">
              {/* Left: Invoice info | Center: Logo | Right: Business info */}
              <div className="flex justify-between items-start">
                <div className="text-[10px] flex-1">
                  <h1 className="text-2xl font-heading font-extrabold text-primary mb-1">INVOICE</h1>
                  <div><span className="text-muted-foreground">Invoice #:</span><span className="ml-1 font-mono">{order.id.slice(0, 13)}</span></div>
                  <div><span className="text-muted-foreground">Date:</span><span className="ml-1">{format(new Date(order.created_at), "MMM dd, yyyy")}</span></div>
                </div>
                <div className="flex-1 flex justify-center">
                  {settings?.business_logo && (
                    <img src={settings.business_logo} alt={settings.business_name || 'Logo'} className="h-16 object-contain" loading="lazy" />
                  )}
                </div>
                <div className="text-right text-[10px] flex-1">
                  <p className="font-semibold text-xs">{settings?.business_name || 'Manna Prints'}</p>
                  <p className="text-muted-foreground">{settings?.business_address || '53 E Maliksi St., Pinyahan, Quezon City'}</p>
                  <p>{settings?.business_phone || '0905 710 9268'}</p>
                  <p>{settings?.business_email || 'mannaclothing.24@gmail.com'}</p>
                </div>
              </div>
            </div>

            {/* 1. Client Information (with Order Details on the right) */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h2 className="text-xs font-heading font-bold mb-1 text-primary">Client Information</h2>
                <div className="space-y-0.5 text-[10px]">
                  <div><span className="text-muted-foreground">Name:</span><span className="ml-1 font-semibold">{order.client_name}</span></div>
                  {order.email && <div><span className="text-muted-foreground">Email:</span><span className="ml-1">{order.email}</span></div>}
                  {order.contact_number && <div><span className="text-muted-foreground">Contact:</span><span className="ml-1">{order.contact_number}</span></div>}
                </div>
              </div>
              <div>
                <h2 className="text-xs font-heading font-bold mb-1 text-primary">Order Details</h2>
                <div className="space-y-0.5 text-[10px]">
                  <div><span className="text-muted-foreground">Order ID:</span><span className="ml-1 font-mono text-[9px]">{order.id.slice(0, 13)}</span></div>
                  <div><span className="text-muted-foreground">Due Date:</span><span className="ml-1 font-semibold text-accent">{safeFormatDate(order.deadline, "MMMM dd, yyyy")}</span></div>
                  <div><span className="text-muted-foreground">Method:</span><span className="ml-1 font-semibold">{order.printing_method}</span></div>
                  <div><span className="text-muted-foreground">Status:</span><span className="ml-1 font-semibold">{order.current_status}</span></div>
                </div>
              </div>
            </div>

            {/* 2. Product Reference (photo) */}
            {hasPhotos && (
              <div className="mb-4">
                <h2 className="text-xs font-heading font-bold mb-1 text-primary">Product Reference</h2>
                <div className="flex gap-3 flex-wrap">
                  {order.photos?.length > 0 ? (
                    order.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`${order.product_type} ${idx + 1}`} className="w-80 h-80 object-cover rounded border border-gray-300" style={{ width: '320px', height: '320px' }} loading="lazy" />
                    ))
                  ) : order.photo ? (
                    <img src={order.photo} alt={order.product_type} className="w-80 h-80 object-cover rounded border border-gray-300" style={{ width: '320px', height: '320px' }} loading="lazy" />
                  ) : null}
                </div>
              </div>
            )}

            {/* 3. Product Specifications */}
            <div className="mb-4">
              <h2 className="text-xs font-heading font-bold mb-1 text-primary">Product Specifications</h2>
              <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Product:</span><span className="ml-1 font-semibold">{order.product_type}</span></div>
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Fabric:</span><span className="ml-1 font-semibold">{order.kind_of_fabric}</span></div>
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Collar:</span><span className="ml-1 font-semibold">{order.collar_type}</span></div>
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Placket:</span><span className="ml-1 font-semibold">{order.placket_style}</span></div>
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Buttons:</span><span className="ml-1 font-semibold">{order.button_color}</span></div>
                <div className="bg-gray-100 p-1.5 rounded"><span className="text-muted-foreground">Total Qty:</span><span className="ml-1 font-semibold">{order.total_quantity} items</span></div>
              </div>
            </div>

            {/* 4. Order Items Table (items 1-10) */}
            <div className="mb-4">
              <h2 className="text-xs font-heading font-bold mb-1 text-primary">Order Items</h2>
              <table className="w-full text-[10px] border border-gray-300">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="text-left p-1.5 border-r border-white/20 w-8">#</th>
                    <th className="text-left p-1.5 border-r border-white/20">Description</th>
                    <th className="text-center p-1.5 border-r border-white/20 w-14">Qty</th>
                    <th className="text-right p-1.5 border-r border-white/20 w-20">Unit Price</th>
                    <th className="text-right p-1.5 w-20">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(needsSecondPage ? order.items.slice(0, 10) : order.items).map((item, index) => (
                    <tr key={index} className="border-b border-gray-300">
                      <td className="p-1.5 border-r border-gray-300">{index + 1}</td>
                      <td className="p-1.5 border-r border-gray-300 font-medium">{item.description}</td>
                      <td className="p-1.5 text-center border-r border-gray-300">{item.quantity}</td>
                      <td className="p-1.5 text-right border-r border-gray-300">{formatCurrency(item.price_per_unit)}</td>
                      <td className="p-1.5 text-right font-semibold">{formatCurrency(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {needsSecondPage && <p className="text-[9px] text-muted-foreground text-right mt-1 italic">Continued on page 2...</p>}
            </div>

            {/* Show payment summary and notes only if single page */}
            {!needsSecondPage && (
              <>
                {/* Payment Summary */}
                <div className="mb-4">
                  <div className="flex justify-end">
                    <div className="w-56">
                      <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                        <span className="text-muted-foreground">
                          {taxSettings.enabled && taxSettings.include_in_price ? 'Subtotal (excl. tax):' : 'Subtotal:'}
                        </span>
                        <span className="font-semibold">{formatCurrency(subtotalBeforeTax)}</span>
                      </div>
                      {taxSettings.enabled && taxSettings.tax_rate > 0 && (
                        <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                          <span className="text-muted-foreground">
                            {taxSettings.tax_name} ({taxSettings.tax_rate}%):
                          </span>
                          <span className="font-semibold">{formatCurrency(taxAmount)}</span>
                        </div>
                      )}
                      {order.down_payment > 0 && (
                        <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                          <span className="text-muted-foreground">Down Payment:</span>
                          <span className="font-semibold text-blue-600">-{formatCurrency(order.down_payment)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                        <span className="text-muted-foreground">Balance Due:</span>
                        <span className="font-semibold text-amber-600">{formatCurrency(Math.max(0, total - (order.down_payment || 0)))}</span>
                      </div>
                      <div className="flex justify-between py-1.5 text-xs">
                        <span className="font-heading font-bold">Total:</span>
                        <span className="font-heading font-bold text-green-600">{formatCurrency(total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 5. Payment Terms & Methods */}
                <div className="mb-4 p-2 border border-gray-300 rounded">
                  <h3 className="text-[10px] font-heading font-bold mb-1">Payment Terms & Methods</h3>
                  {taxSettings.enabled && taxSettings.tax_id && (
                    <p className="text-[9px] text-muted-foreground mb-1">Tax ID: {taxSettings.tax_id}</p>
                  )}
                  {settings?.payment_terms && <p className="text-[9px] text-muted-foreground mb-1">{settings.payment_terms}</p>}
                  {settings?.payment_methods?.length > 0 && (
                    <div className="space-y-0.5">
                      <p className="text-[9px] font-semibold">Available Payment Methods:</p>
                      {settings.payment_methods.map((method, index) => (
                        <div key={index} className="text-[9px]">
                          <span className="font-medium">{method.name}:</span> <span className="text-muted-foreground">{method.details}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="pt-2 border-t border-gray-300 text-center text-[9px] text-muted-foreground">
                  <p className="font-semibold mb-0.5">Thank you for your business!</p>
                  <p>Invoice generated on {format(new Date(), "MMMM dd, yyyy 'at' hh:mm a")}</p>
                </div>
              </>
            )}

            {needsSecondPage && (
              <div className="absolute bottom-4 right-4 text-[9px] text-muted-foreground">Page 1 of 2</div>
            )}
          </div>

          {/* Page 2 - Only if more than 10 items */}
          {needsSecondPage && (
            <div 
              className="a4-page bg-white mx-auto mb-8 shadow-lg relative"
              style={{ width: `${A4_WIDTH_PX}px`, minHeight: `${A4_HEIGHT_PX}px`, padding: `${PAGE_PADDING}px`, boxSizing: 'border-box' }}
            >
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-300">
                <h2 className="text-sm font-heading font-bold text-primary">INVOICE (continued)</h2>
                <p className="text-[10px] text-muted-foreground">Invoice #{order.id.slice(0, 13)}</p>
              </div>

              {/* Remaining Items (11th onwards) */}
              <div className="mb-4">
                <table className="w-full text-[10px] border border-gray-300">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="text-left p-1.5 border-r border-white/20 w-8">#</th>
                      <th className="text-left p-1.5 border-r border-white/20">Description</th>
                      <th className="text-center p-1.5 border-r border-white/20 w-14">Qty</th>
                      <th className="text-right p-1.5 border-r border-white/20 w-20">Unit Price</th>
                      <th className="text-right p-1.5 w-20">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.slice(10).map((item, index) => (
                      <tr key={index} className="border-b border-gray-300">
                        <td className="p-1.5 border-r border-gray-300">{index + 11}</td>
                        <td className="p-1.5 border-r border-gray-300 font-medium">{item.description}</td>
                        <td className="p-1.5 text-center border-r border-gray-300">{item.quantity}</td>
                        <td className="p-1.5 text-right border-r border-gray-300">{formatCurrency(item.price_per_unit)}</td>
                        <td className="p-1.5 text-right font-semibold">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Payment Summary */}
              <div className="mb-4">
                <div className="flex justify-end">
                  <div className="w-56">
                    <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                      <span className="text-muted-foreground">
                        {taxSettings.enabled && taxSettings.include_in_price ? 'Subtotal (excl. tax):' : 'Subtotal:'}
                      </span>
                      <span className="font-semibold">{formatCurrency(subtotalBeforeTax)}</span>
                    </div>
                    {taxSettings.enabled && taxSettings.tax_rate > 0 && (
                      <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                        <span className="text-muted-foreground">
                          {taxSettings.tax_name} ({taxSettings.tax_rate}%):
                        </span>
                        <span className="font-semibold">{formatCurrency(taxAmount)}</span>
                      </div>
                    )}
                    {order.down_payment > 0 && (
                      <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                        <span className="text-muted-foreground">Down Payment:</span>
                        <span className="font-semibold text-blue-600">-{formatCurrency(order.down_payment)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-1 border-b border-gray-300 text-[10px]">
                      <span className="text-muted-foreground">Balance Due:</span>
                      <span className="font-semibold text-amber-600">{formatCurrency(Math.max(0, total - (order.down_payment || 0)))}</span>
                    </div>
                    <div className="flex justify-between py-1.5 text-xs">
                      <span className="font-heading font-bold">Total:</span>
                      <span className="font-heading font-bold text-green-600">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Terms & Methods */}
              <div className="mb-4 p-2 border border-gray-300 rounded">
                <h3 className="text-[10px] font-heading font-bold mb-1">Payment Terms & Methods</h3>
                {taxSettings.enabled && taxSettings.tax_id && (
                  <p className="text-[9px] text-muted-foreground mb-1">Tax ID: {taxSettings.tax_id}</p>
                )}
                {settings?.payment_terms && <p className="text-[9px] text-muted-foreground mb-1">{settings.payment_terms}</p>}
                {settings?.payment_methods?.length > 0 && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] font-semibold">Available Payment Methods:</p>
                    {settings.payment_methods.map((method, index) => (
                      <div key={index} className="text-[9px]">
                        <span className="font-medium">{method.name}:</span> <span className="text-muted-foreground">{method.details}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="pt-2 border-t border-gray-300 text-center text-[9px] text-muted-foreground">
                <p className="font-semibold mb-0.5">Thank you for your business!</p>
                <p>Invoice generated on {format(new Date(), "MMMM dd, yyyy 'at' hh:mm a")}</p>
              </div>

              <div className="absolute bottom-4 right-4 text-[9px] text-muted-foreground">Page 2 of 2</div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
